import React, { useState } from 'react';
import Select, { components } from 'react-select';
import Createable from 'react-select/creatable';
import styles from './select.module.css';
import cx from 'classnames';
import useScreenSize from 'hooks/size';
import { Icon } from 'assets/icons';
import { Checkbox } from '../input/checkbox';

const custom_theme = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    // neutral10: 'var(--neutral-dark-4)',
    neutral80: '#FFFFFF',
    neutral90: '#FFFFFF'
  }
});

const CustomClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props} className={cx(styles.clearIndicator)}>
      <Icon width={18} height={18} name="close" />
    </components.ClearIndicator>
  );
};

const Control = ({ selectProps, ...props }) => {
  const error_class = selectProps.error ? styles.errorBorder : '';
  const dark_class = selectProps.dark ? styles.controlDark : '';
  return <components.Control {...props} className={cx(styles.control, error_class, dark_class)} />;
};

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props} className={cx(styles.dropdownIndicator)}>
      <Icon width={20} height={20} name="expand" />
    </components.DropdownIndicator>
  );
};

const IndicatorSeparator = (props) => {
  return <span {...props} className={cx(styles.indicatorSeparator)} />;
};

const Option = (props) => {
  const { isMulti, isSelected } = props;
  if (isMulti)
    return (
      <components.Option {...props}>
        <div
          onClick={(e) => {
            e.preventDefault();
          }}
          className={styles.menulist}
        >
          <Checkbox
            checked={isSelected}
            onClick={() => {}}
            label={props.children}
            checkboxClass={styles.optionCheckbox}
          />
        </div>
      </components.Option>
    );
  return <components.Option {...props} className={styles.option} />;
};

const Menu = (props) => {
  return (
    <components.Menu {...props} className={styles.menu}>
      {props.children}
    </components.Menu>
  );
};

const MenuList = (props) => {
  const {
    options,
    getValue,
    selectProps: { onChange }
  } = props;
  const handleBulkSelect = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isAllOptionsSelected()) {
      onChange([]);
      return;
    }
    onChange(options);
  };

  const isAllOptionsSelected = () => {
    return getValue().length === options.length;
  };

  if (props.isMulti)
    return (
      <components.MenuList {...props}>
        <div
          onClick={(e) => {
            handleBulkSelect(e);
            e.preventDefault();
          }}
          className={cx(styles.menulist, styles.menulistHeader)}
        >
          <Checkbox checked={isAllOptionsSelected()} label="Select All" />
        </div>
        {props.children}
      </components.MenuList>
    );

  return <components.MenuList {...props} />;
};

export const MultiSelect = ({
  label = '',
  input_id = '',
  defaultValue = [],
  options = [],
  sort_options_by = 'label',
  placeholder,
  onChange,
  value,
  isMulti,
  required = false,
  hideSelectedOptions = false,
  isCreatable = false,
  isClearable,
  error = '',
  disabled = false,
  isSorted = true,
  dark = false,
  menuPlacement = 'auto',
  className = ''
}) => {
  const { isDesktop } = useScreenSize();

  const custom_styles = {
    menuList: (provided) => ({
      ...provided,
      maxWidth: '250px'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'var(--blue-light-5)',
      borderRadius: '5px',
      color: 'var(--blue-dark)',
      fontSize: !isDesktop ? '12px' : '14px',
      padding: '5px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--blue-dark)',
      fontSize: !isDesktop ? '12px' : '14px',
      padding: '0'
    }),
    singleValue: (provided) => ({
      ...provided,
      borderRadius: '5px',
      backgroundColor: 'var(--blue-light-5)',
      color: 'var(--blue-dark)',
      fontSize: !isDesktop ? '12px' : '14px',
      padding: '5px 15px'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
      maxHeight: '28px !important',
      overflowY: 'hidden'
    }),
    input: (provided) => ({
      ...provided,
      paddingTop: '0',
      paddingBottom: '0',
      margin: '0',
      color: 'var(--neutral-dark-2)'
    }),
    option: (provided) => ({
      ...provided,
      width: '100% !important',
      color: 'var(--neutral-dark-2)',
      backgroundColor: '#fff'
    }),
    indicatorContainer: (provided) => ({
      ...provided,
      height: '28px !important',
      width: '28px !important'
    })
  };

  const dark_styles = {
    menuList: (provided) => ({ ...provided }),
    multiValue: custom_styles.multiValue,
    multiValueLabel: custom_styles.multiValueLabel,
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 14px'
    }),
    singleValue: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      color: 'white',
      fontSize: !isDesktop ? '12px' : '14px',
      padding: '0'
    }),
    input: (provided) => ({
      ...provided,
      paddingTop: '0',
      paddingBottom: '0',
      margin: '0',
      color: 'white'
    }),
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      width: '100% !important',
      color: isSelected ? 'var(--blue-primary)' : 'white',
      backgroundColor: isSelected
        ? 'rgba(67, 165, 255, 0.15)'
        : isFocused
        ? 'rgba(255, 255, 255, 0.07)'
        : 'transparent'
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--blue-dark)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '10px'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgba(255, 255, 255, 0.35)'
    }),
    indicatorContainer: custom_styles.indicatorContainer
  };

  const sortOptions = (sort_options_by) => {
    if (!isSorted) return options;
    if (sort_options_by === 'value') {
      return options.sort((a, b) => a.value - b.value);
    }

    return options.sort((a, b) => a.label?.localeCompare(b.label));
  };

  const [isPlaceHolderValueSet, setIsPlaceHolderValueSet] = useState(isMulti);
  const handleBlur = () => {
    if (!isMulti) return;
    setIsPlaceHolderValueSet(true);
  };
  const handleFocus = () => {
    if (!isMulti) return;
    setIsPlaceHolderValueSet(false);
  };

  const formatValue = () => {
    const formattedValue = [];
    if (value && value.length > 0) {
      formattedValue.push({
        label: value[0].label.length > 12 ? `${value[0].label.slice(0, 12)}...` : value[0].label,
        value: value[0].value
      });

      if (value.length > 1) {
        formattedValue.push({
          label: `+ ${value.length - 1} more`,
          value: value.length - 1
        });
      }
    }
    return formattedValue;
  };

  if (isCreatable) {
    return (
      <div className={styles.formGroup}>
        {label && (
          <label htmlFor={input_id}>
            {label}
            {required && <span className={styles.error}>*</span>}
          </label>
        )}
        <Createable
          isMulti={isMulti}
          className={cx(styles.rselect, { [styles.errorBorder]: error }, className)}
          options={sortOptions(sort_options_by)}
          onChange={onChange}
          defaultValue={defaultValue}
          value={value}
          components={{
            ClearIndicator: CustomClearIndicator,
            Control,
            DropdownIndicator,
            IndicatorSeparator,
            Menu,
            MenuList,
            Option
          }}
          closeMenuOnSelect={!isMulti}
          menuPlacement={menuPlacement}
          placeholder={placeholder}
          styles={custom_styles}
          theme={custom_theme}
          hideSelectedOptions={hideSelectedOptions}
          error={error}
          isDisabled={disabled}
          isClearable={isClearable}
        />
        {error && <span className={styles.error}>{error}</span>}
      </div>
    );
  }
  // console.log({ error }, { required });

  return (
    <>
      <div className={styles.formGroup}>
        {label && (
          <label htmlFor={input_id}>
            {label}
            {required && <span className={styles.error}>*</span>}
          </label>
        )}
        <Select
          isMulti={isMulti}
          className={cx(styles.rselect, { [styles.errorBorder]: error }, className)}
          options={sortOptions(sort_options_by)}
          onChange={onChange}
          defaultValue={defaultValue}
          value={isPlaceHolderValueSet ? formatValue() : value}
          components={{
            ClearIndicator: CustomClearIndicator,
            Control,
            DropdownIndicator,
            IndicatorSeparator,
            Menu,
            MenuList,
            Option
          }}
          closeMenuOnSelect={!isMulti}
          menuPlacement={menuPlacement}
          placeholder={placeholder}
          styles={dark ? dark_styles : custom_styles}
          onBlur={handleBlur}
          onFocus={handleFocus}
          theme={custom_theme}
          hideSelectedOptions={hideSelectedOptions}
          error={error}
          dark={dark}
          isDisabled={disabled}
          isClearable={isClearable}
        />
        {error && <span className={styles.error}>{error}</span>}
      </div>
    </>
  );
};
